import lottie, {
  type AnimationDirection,
  type AnimationEventName,
  type AnimationItem,
  type AnimationSegment,
} from "lottie-web";
import React, { useEffect, useRef, useState } from "react";

const fetchCache = new Map<string, Promise<any>>();

// De-dupe concurrent/repeat warm-ups so each unique font spec is loaded once.
const fontWarmCache = new Map<string, Promise<unknown>>();

const warmFonts = async (fonts?: string | string[]): Promise<void> => {
  if (!fonts || typeof document === "undefined" || !document.fonts) {
    return;
  }

  const specs = Array.isArray(fonts) ? fonts : [fonts];

  await Promise.all(
    specs.map((spec) => {
      if (!fontWarmCache.has(spec)) {
        // document.fonts.load resolves once the matching @font-face is ready,
        // so lottie-web measures glyph widths against the real font.
        fontWarmCache.set(
          spec,
          document.fonts.load(spec).catch(() => {}),
        );
      }

      return fontWarmCache.get(spec)!;
    }),
  );
};

export { type AnimationSegment };

// queueSegments catch-up: each hop still waiting in lottie's segment queue
// speeds up the glide by this factor (so the cursor closes the gap on a fast
// typist), capped so it never blurs past readable. Speed eases back to base as
// the queue drains, since the count shrinks on every segmentStart.
const QUEUE_SPEEDUP_PER_HOP = 0.5;
const QUEUE_MAX_SPEEDUP = 3;

interface LottieProps {
  path: string;
  delay?: number;
  segments?: AnimationSegment | AnimationSegment[];
  speed?: number;
  loop?: boolean | number;
  direction?: AnimationDirection;
  autoplay?: boolean;
  bounds?: React.CSSProperties;
  onCompleteAnimation?: () => void;
  onLoadComplete?: () => void;
  onSegmentStart?: () => void;
  start?: boolean;
  eventListener?: [AnimationEventName, (event: any) => void];
  onDataFetched?: (data: any) => any;
  /**
   * CSS font shorthand(s) (e.g. `'700 82px "Roboto Mono"'`) to warm via
   * `document.fonts.load` before the animation is built. lottie-web measures
   * text-layer glyph widths at build time; if the @font-face isn't loaded yet
   * the measurement falls back to a default font and center/right-justified
   * text ends up offset. Waiting here removes that race.
   */
  fonts?: string | string[];
  /**
   * When true, a `segments` change is appended to lottie's segment queue
   * (`playSegments(..., false)`) instead of interrupting the current segment.
   * Playback is only forced when the animation is idle, so rapid updates play
   * back-to-back without skipping frames. Used by the typing cursor, which must
   * glide continuously even when keys are pressed faster than one hop animates.
   */
  queueSegments?: boolean;
}

export const Lottie: React.FC<LottieProps> = ({
  path,
  delay,
  segments,
  speed = 1,
  loop = false,
  direction = 1,
  bounds = {},
  onCompleteAnimation,
  onLoadComplete,
  onSegmentStart,
  start = true,
  eventListener,
  onDataFetched,
  fonts,
  queueSegments = false,
  ...props
}) => {
  const lottieDiv = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<AnimationItem | null>(null);
  const playTimeout = useRef<any>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [styleBounds, setStyleBounds] = useState<React.CSSProperties>(bounds);

  // Read live each call so the segmentStart listener (registered once) always
  // sees the current prop values rather than the ones from its render.
  const queueSegmentsRef = useRef(queueSegments);
  queueSegmentsRef.current = queueSegments;
  const baseSpeedRef = useRef(speed);
  baseSpeedRef.current = speed;

  // Scale playback to lottie's pending-segment count so a backlog of queued
  // hops plays faster, then settles back to base speed as the queue empties.
  const applyQueueSpeed = () => {
    const anim = lottieRef.current;
    if (!queueSegmentsRef.current || !anim) {
      return;
    }
    const queued = (anim as unknown as { segments: unknown[] }).segments.length;
    const factor = Math.min(
      1 + queued * QUEUE_SPEEDUP_PER_HOP,
      QUEUE_MAX_SPEEDUP,
    );
    anim.setSpeed(baseSpeedRef.current * factor);
  };

  useEffect(() => {
    setLoaded(false);
    lottieInit().then((res: AnimationItem | null) => {
      if (res && res.isLoaded) {
        if (bounds) {
          setStyleBounds(bounds);
        }

        if (lottieRef.current) {
          lottieRef.current.setDirection(direction);
        }

        setLoaded(true);
      }
    });

    return () => {
      if (playTimeout.current) {
        clearTimeout(playTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    if (loaded && onLoadComplete) {
      onLoadComplete();
    }

    if (loaded && start && lottieRef.current) {
      lottieRef.current.setSpeed(speed);
      lottieRef.current.setDirection(direction);
      playTimeout.current = setTimeout(
        () => {
          if (segments) {
            // queueSegments: append while a segment is mid-play so it finishes
            // before the next runs (no frame skip); only force when idle, where
            // a queued push wouldn't get adjusted onto the new segment.
            const force = queueSegments
              ? (lottieRef.current?.isPaused ?? true)
              : true;
            lottieRef.current?.playSegments(segments, force);
            applyQueueSpeed();
          } else {
            lottieRef.current?.play();
          }
        },
        (delay || 0) * 1000,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, start, segments]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    lottieRef.current?.setDirection(direction);
    lottieRef.current?.setSpeed(speed);
    lottieRef.current?.play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, speed]);

  const lottieInit = async (): Promise<AnimationItem | null> => {
    await warmFonts(fonts);

    const data = await fetchData();

    if (lottieRef.current) {
      lottieRef.current.destroy();
    }

    // Build paused on the target segment's first frame rather than frame 0, so
    // the SVG never paints the neutral frame-0 pose before `playSegments` snaps
    // the playhead into the segment (the source of the handoff flicker).
    const initialSegment = segments
      ? Array.isArray(segments[0])
        ? (segments[0] as AnimationSegment)
        : (segments as AnimationSegment)
      : undefined;

    lottieRef.current = lottie.loadAnimation({
      loop,
      container: lottieDiv.current!,
      renderer: "svg",
      autoplay: false,
      animationData: data,
      initialSegment,
    });

    lottieRef.current.addEventListener("complete", function () {
      if (onCompleteAnimation) {
        onCompleteAnimation();
      }
    });

    if (eventListener && lottieRef.current) {
      lottieRef.current.addEventListener(...eventListener);
    }

    lottieRef.current.addEventListener("segmentStart", function () {
      // Each hop that starts has been shifted off the queue, so re-deriving the
      // speed here eases it back toward base as the backlog drains.
      applyQueueSpeed();
      if (onSegmentStart) {
        onSegmentStart();
      }
    });

    return lottieRef.current;
  };

  const fetchData = async (): Promise<any> => {
    if (!fetchCache.has(path)) {
      fetchCache.set(
        path,
        fetch(path).then((r) => r.json()),
      );
    }

    const data = await fetchCache.get(path)!;

    return onDataFetched ? onDataFetched(data) : data;
  };

  return <div style={styleBounds} ref={lottieDiv} {...props} />;
};
